/**
 * Dashboard admin: contadores operativos (usuarios por rol, niños, baterías por
 * estado, baterías QA, palabras del banco, licencias activas) + las últimas 10
 * baterías. Todo lectura. Cada fuente es una lectura independiente: si una falla
 * (p. ej. sin seed del banco) el resto del tablero sigue mostrándose.
 */
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../api/endpoints.js";
import { useQuery } from "../../hooks/useQuery.js";
import { EstadoBadge, PerfilBadge, QaTag, fmtFechaCorta } from "./ui.js";
import s from "./admin.module.css";

function Tile({ value, label, sub }: { value: number | string; label: string; sub?: string }) {
  return (
    <div className={s.tile}>
      <div className={s.tileValue}>{value}</div>
      <div className={s.tileLabel}>{label}</div>
      {sub ? <div className={s.tileSub}>{sub}</div> : null}
    </div>
  );
}

export function AdminDashboard() {
  const navigate = useNavigate();
  const stats = useQuery(() => adminApi.stats());
  const bStats = useQuery(() => adminApi.bateriasStats());
  const wb = useQuery(() => adminApi.wordBankStats());
  const lic = useQuery(() => adminApi.licenciasStats());
  const ultimas = useQuery(() => adminApi.baterias({ limit: 10 }));

  const u = stats.data?.usuarios;
  const porEstado = bStats.data?.porEstado ?? {};
  const estados = Object.entries(porEstado).sort((a, b) => b[1] - a[1]);

  return (
    <>
      <div className={s.tiles}>
        <Tile value={u?.total ?? "—"} label="Usuarios" sub={`${u?.activos ?? 0} activos`} />
        <Tile value={u?.padre ?? "—"} label="Padres" />
        <Tile value={u?.terapeuta ?? "—"} label="Terapeutas" />
        <Tile value={u?.admin ?? "—"} label="Admins" />
        <Tile value={stats.data?.ninos ?? "—"} label="Niños" />
        <Tile
          value={bStats.data?.total ?? "—"}
          label="Baterías"
          sub={`${bStats.data?.qa ?? 0} en modo prueba (QA)`}
        />
        <Tile value={wb.data?.total ?? "—"} label="Palabras del banco" />
        <Tile value={lic.data?.licenciasActivas ?? "—"} label="Licencias activas (legacy)" />
      </div>

      <div className={s.sectionTitle}>Baterías por estado</div>
      <div className={s.tiles}>
        {estados.length === 0 ? (
          <div className={s.tileSub}>Sin baterías todavía.</div>
        ) : (
          estados.map(([estado, n]) => (
            <div key={estado} className={s.tile}>
              <div className={s.tileValue} style={{ fontSize: 24 }}>
                {n}
              </div>
              <div style={{ marginTop: 4 }}>
                <EstadoBadge estado={estado as never} />
              </div>
            </div>
          ))
        )}
      </div>

      <div className={s.sectionTitle}>Últimas 10 baterías</div>
      <div className={s.panel}>
        <div className={s.tableWrap}>
          <table className={s.table}>
            <thead>
              <tr>
                <th>Niño</th>
                <th>Banda</th>
                <th>Estado</th>
                <th>Perfil</th>
                <th>Creada</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(ultimas.data?.items ?? []).map((b) => (
                <tr
                  key={b.id}
                  className={s.rowClickable}
                  onClick={() => navigate(`/admin/evaluaciones/${b.id}`)}
                >
                  <td>
                    <span className={s.mono}>{b.childId.slice(0, 8)}</span>{" "}
                    {b.modoPrueba ? <QaTag /> : null}
                  </td>
                  <td>{b.banda}</td>
                  <td>
                    <EstadoBadge estado={b.estado} />
                  </td>
                  <td>
                    <PerfilBadge profile={b.profile} />
                  </td>
                  <td className={s.muted}>{fmtFechaCorta(b.createdAt)}</td>
                  <td className={s.muted}>›</td>
                </tr>
              ))}
              {ultimas.data && ultimas.data.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className={s.empty}>
                    Sin baterías.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
